/**
 * Spec Phase 12 — IDF Coverage dock (BUG-039).
 *
 * Live mirror of the §A.10 export-appendix audit. Reads
 * ``GET /spec/idf-coverage`` (deterministic SQL + string match,
 * no LLM), shows a progress bar + Covered / Missing groups so
 * drafters can see which inventor-disclosed concepts the spec
 * still hasn't addressed.
 *
 * Refresh strategy:
 *   - First load: when the dock first expands (or first mount).
 *   - Manual: refresh button calls with ``forceRefresh=true``.
 *   - Auto: 30s debounce after a spec paragraph mutation
 *     (``paragraphsBySection`` changes). Server caches 5 min, so
 *     repeat refreshes return immediately.
 *
 * "Add to section" button dispatches a window event the
 * ``ActionBar`` listens for; the steering box pre-fills with
 * "Include coverage of: <concept>" and expands so the drafter
 * can review before clicking Generate.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw, ListChecks, Check, X, ChevronDown, BookOpen, Loader2,
  ArrowRight,
} from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type {
  SpecIdfConcept, SpecCoverageBlock, SpecSectionRow,
} from '@/api/client';
import Dock from './Dock';

const PREFILL_EVENT = 'spec.action-bar.prefill-steering';
const SCROLL_EVENT = 'spec.scroll-to-section';
const AUTO_REFRESH_DEBOUNCE_MS = 30_000;
// Coverage "Add to section" always targets the detailed
// description of embodiments. Patent practice: every claim
// element must be supported in the detailed description, so
// claim-coverage gaps belong here regardless of which section
// is currently active in the editor.
const COVERAGE_TARGET_KIND = 'detailed_desc_embodiments';
// Cap the batch "Add all missing" payload so the steering text
// stays manageable for the LLM (10 directives at most). The
// drafter can rerun the batch button after each generate to
// chip away at longer missing lists.
const BATCH_ADD_LIMIT = 10;

type CoverageTab = 'idf' | 'claims';

export default function IdfCoverageDock(): JSX.Element {
  const data = useSpecStore((s) => s.idfCoverage);
  const status = useSpecStore((s) => s.idfCoverageStatus);
  const loadIdfCoverage = useSpecStore((s) => s.loadIdfCoverage);
  const loadLastIdfCoverage = useSpecStore((s) => s.loadLastIdfCoverage);
  const matterId = useSpecStore((s) => s.matterId);
  const paragraphsBySection = useSpecStore((s) => s.paragraphsBySection);
  // FIX 1 — auto-route "Add to section" through the detailed-
  // description section. Sections list + nav callbacks are now
  // dock-level state so both single-item and batch buttons can
  // share the routing logic.
  const sections = useSpecStore((s) => s.sections);
  const setActiveSection = useSpecStore((s) => s.setActiveSection);
  const showToast = useSpecStore((s) => s.showToast);
  // When Generate-Everything is in flight, the steering load
  // is still valuable (drafter queues the directive) but the
  // toast copy should explicitly tell them to wait for GE to
  // finish before clicking Generate. Without this hint the
  // drafter clicks Generate, hits the section-not-empty 409
  // from the GE-just-wrote-this-section path, and sees the
  // OverwriteConfirmDialog — which reads as "Add-to-Section
  // triggered a regenerate prompt".
  const geRunning = useSpecStore((s) => {
    const st = s.generateEverythingStatus;
    return st?.status === "queued" || st?.status === "running";
  });
  const targetSection = useMemo<SpecSectionRow | null>(
    () => sections.find((s) => s.section_kind === COVERAGE_TARGET_KIND) || null,
    [sections],
  );
  const targetLabel = targetSection
    ? (
        targetSection.custom_title
        || targetSection.title
        || 'Detailed Description of Embodiments'
      )
    : 'Detailed Description of Embodiments';

  const [coveredOpen, setCoveredOpen] = useState<boolean>(false);
  const [missingOpen, setMissingOpen] = useState<boolean>(true);
  // BUG-040 — IDF / Claims tab toggle. Default to IDF (the
  // original BUG-039 view); drafters click Claims for the
  // claim-side audit.
  const [tab, setTab] = useState<CoverageTab>('idf');
  const debounceRef = useRef<number | null>(null);
  // BUG-047 — has the audit ever been kicked off this session?
  // Drives the "Run audit" CTA visibility + auto-refresh gate.
  // We deliberately DO NOT auto-fetch on dock-open any more;
  // drafters trigger explicitly (per the cue's spec).
  const hasRunRef = useRef<boolean>(false);
  if (data && !hasRunRef.current) hasRunRef.current = true;

  // BUG-039 — auto-refresh 30s after any paragraph mutation. Only
  // fires after the drafter has explicitly run the audit at
  // least once; until then we show the "Run audit" CTA and stay
  // quiet on the network.
  useEffect(() => {
    if (!hasRunRef.current) return;
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      void loadIdfCoverage(true);
    }, AUTO_REFRESH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [paragraphsBySection, loadIdfCoverage]);

  // NB-23 — after a reload the dock showed "not yet run" although the
  // drafter had run the audit. Ask for it again on load; a matter
  // never audited stays on the Run button (BUG-047's opt-in).
  useEffect(() => {
    if (matterId) void loadLastIdfCoverage();
  }, [matterId, loadLastIdfCoverage]);

  const onRefresh = useCallback(() => {
    hasRunRef.current = true;
    void loadIdfCoverage(true);
  }, [loadIdfCoverage]);

  // FIX 1 — "Add to section" now navigates to the detailed-
  // description section, loads a generation-ready steering
  // directive, and confirms with a toast. The drafter still
  // has to click Generate manually (we don't want to fire LLM
  // calls behind their back), but every other ambiguity that
  // made the previous prefill-only flow confusing is gone.
  const _routeToTargetSection = useCallback((): boolean => {
    if (!targetSection) {
      showToast(
        `Coverage routing target "${targetLabel}" not found in this `
        + 'matter. Check the section list.',
      );
      return false;
    }
    setActiveSection(targetSection.id);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SCROLL_EVENT, {
        detail: { id: targetSection.id },
      }));
    }
    return true;
  }, [targetSection, targetLabel, setActiveSection, showToast]);

  const onAddToSection = useCallback((concept: string) => {
    if (!_routeToTargetSection()) return;
    const directive =
      `Add explicit coverage of: ${concept}. `
      + 'Reference this concept by name and link it to a '
      + 'specific claim element.';
    window.dispatchEvent(new CustomEvent(PREFILL_EVENT, {
      detail: { text: directive },
    }));
    if (targetSection && (targetSection.paragraph_count ?? 0) === 0) {
      showToast(
        `${targetLabel} has no content yet. Generate it first, `
        + 'then add coverage.',
      );
    } else if (geRunning) {
      showToast(
        `Steering loaded for ${targetLabel}. `
        + "Generate-Everything is running — click Generate "
        + "after it completes.",
      );
    } else {
      showToast(
        `Steering loaded for ${targetLabel}. `
        + 'Click Generate with this steering to add coverage.',
      );
    }
  }, [_routeToTargetSection, showToast, targetLabel, targetSection, geRunning]);

  // FIX 5 — batch "Add all missing". Caps at BATCH_ADD_LIMIT
  // concepts per click; the drafter can re-click after each
  // generate to chip away at longer lists. The directive
  // explicitly tells the LLM to anchor each concept by name so
  // the next audit pass actually flips them to covered.
  const onAddAllMissing = useCallback((missingRows: SpecIdfConcept[]) => {
    if (missingRows.length === 0) return;
    if (!_routeToTargetSection()) return;
    const slice = missingRows.slice(0, BATCH_ADD_LIMIT);
    const listing = slice.map((r) => `"${r.concept}"`).join(', ');
    const truncatedSuffix =
      missingRows.length > BATCH_ADD_LIMIT
        ? ` (showing first ${BATCH_ADD_LIMIT} of ${missingRows.length}; `
          + 'add the remainder on the next batch).'
        : '';
    const directive =
      'Add explicit coverage of ALL of the following: '
      + `${listing}. Each must appear at least once by name in this `
      + `section and link to a specific claim element${truncatedSuffix}`;
    window.dispatchEvent(new CustomEvent(PREFILL_EVENT, {
      detail: { text: directive },
    }));
    if (targetSection && (targetSection.paragraph_count ?? 0) === 0) {
      showToast(
        `${targetLabel} has no content yet. Generate it first, `
        + 'then add coverage.',
      );
    } else if (geRunning) {
      showToast(
        `Batch steering loaded for ${targetLabel} — ${slice.length} `
        + "concept(s). Generate-Everything is running — click "
        + "Generate after it completes.",
      );
    } else {
      showToast(
        `Batch steering loaded for ${targetLabel} — ${slice.length} `
        + 'concept(s). Click Generate with this steering.',
      );
    }
  }, [_routeToTargetSection, showToast, targetLabel, targetSection, geRunning]);

  // BUG-040 — pick the active block (IDF or claim) for the
  // headline + groups. Both blocks share the SpecCoverageBlock
  // shape so the renderer below is single-source.
  const idfBlock: SpecCoverageBlock = useMemo(() => ({
    total_concepts: data?.total_concepts ?? 0,
    covered:        data?.covered ?? 0,
    missing:        data?.missing ?? 0,
    coverage_pct:   data?.coverage_pct ?? 0,
    concepts:       data?.concepts ?? [],
  }), [data]);
  const claimBlock: SpecCoverageBlock = useMemo(() => (
    data?.claim_coverage || {
      total_concepts: 0, covered: 0, missing: 0,
      coverage_pct: 0, concepts: [],
    }
  ), [data]);
  const active = tab === 'idf' ? idfBlock : claimBlock;

  const covered = active.covered;
  const missing = active.missing;
  const total = active.total_concepts;
  const pct = active.coverage_pct;
  const barColour = pct >= 80
    ? 'bg-emerald-500'
    : pct >= 60
      ? 'bg-amber-500'
      : 'bg-rose-500';

  const meta: string | undefined = (() => {
    if (status === 'loading' && !data) return 'Checking…';
    if (status === 'error') return 'Failed to load.';
    // BUG-047 — never-run state has its own copy + a prominent
    // CTA in the body. Header meta stays empty so it doesn't
    // duplicate the body message.
    if (!data) return undefined;
    // Tab-aware empty meta. The body block below handles the
    // expanded empty state per tab.
    if (tab === 'idf' && !data.has_idf_summary) {
      return 'Stage 1 IDF analysis not complete.';
    }
    if (tab === 'claims' && data.has_frozen_claims === false) {
      return 'No frozen claims yet.';
    }
    if (!data.has_drafted_content) {
      return 'No spec content yet — generate sections to start the audit.';
    }
    if (data.last_checked) {
      return `${pct}% covered · last checked ${_relativeTime(data.last_checked)}`;
    }
    return undefined;
  })();

  const groups = _splitGroups(active.concepts);

  // Pill counts for the IDF / Claims tabs in the header: covered of
  // total, the same way round as the bar below. NB-14: a bare
  // missing/total pill read "Claims 0/18" for a fully covered claim
  // set, which looked like nothing was covered.
  const idfMissing = idfBlock.missing;
  const idfTotal = idfBlock.total_concepts;
  const claimMissing = claimBlock.missing;
  const claimTotal = claimBlock.total_concepts;

  // BUG-047 — never-run state. Show a prominent "Run audit" CTA
  // and skip every other panel surface. Drafters opt in
  // explicitly; we don't auto-fetch on dock-open any more.
  const isLoading = status === 'loading';
  const hasData = !!data;
  const isErrored = status === 'error' && !data;

  return (
    <Dock
      dockKey="idfCoverage"
      icon={<ListChecks className="h-3.5 w-3.5 text-zinc-500" aria-hidden />}
      title="Coverage Audit"
      pillCount={total > 0 ? total : undefined}
      meta={meta}
      testId="spec-dock-idf-coverage"
    >
      {!hasData && (
        <div
          className="flex flex-col items-center gap-2 rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-3 py-4 text-center"
          data-testid="spec-coverage-never-run"
        >
          <p className="text-[11px] text-zinc-600">
            {isErrored
              ? 'Last attempt failed — try again.'
              : 'IDF coverage audit not yet run.'}
          </p>
          <button
            type="button"
            className="inline-flex h-8 items-center gap-1 rounded-sm bg-[#002FA7] px-3 text-xs font-medium text-white hover:bg-[#001f6f] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onRefresh}
            disabled={isLoading}
            data-testid="spec-coverage-run"
          >
            {isLoading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <RefreshCw className="h-3 w-3" />}
            {isLoading ? 'Checking…' : 'Run IDF Coverage Audit'}
          </button>
          <p className="text-[10px] text-zinc-400">
            Pure SQL + string match · no LLM call.
          </p>
        </div>
      )}
      {/* BUG-040 — IDF / Claims tab toggle. Per-tab pill shows
          missing/total so drafters see the actionable count for
          the inactive tab without switching. */}
      {data && (
        <div
          className="mb-2 inline-flex rounded-sm border border-zinc-200 bg-zinc-50 p-0.5 text-[11px]"
          role="tablist"
          aria-label="Coverage source"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'idf'}
            className={[
              'rounded-sm px-2 py-0.5 font-medium transition-colors',
              tab === 'idf'
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-800',
            ].join(' ')}
            onClick={() => setTab('idf')}
            data-testid="spec-coverage-tab-idf"
          >
            IDF
            {idfTotal > 0 && (
              <span
                className="ml-1 font-mono text-[10px] text-zinc-400"
                title={`${idfTotal - idfMissing} of ${idfTotal} covered`}
              >
                {idfTotal - idfMissing}/{idfTotal}
              </span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'claims'}
            className={[
              'rounded-sm px-2 py-0.5 font-medium transition-colors',
              tab === 'claims'
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-800',
            ].join(' ')}
            onClick={() => setTab('claims')}
            data-testid="spec-coverage-tab-claims"
          >
            Claims
            {claimTotal > 0 && (
              <span
                className="ml-1 font-mono text-[10px] text-zinc-400"
                title={`${claimTotal - claimMissing} of ${claimTotal} covered`}
              >
                {claimTotal - claimMissing}/{claimTotal}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Progress bar + refresh button. */}
      {data && _isTabReady(data, tab) && (
        <div className="mb-3 flex items-center gap-2">
          <div className="flex-1">
            <div className="h-1.5 w-full overflow-hidden rounded-sm bg-zinc-100">
              <div
                className={`h-full ${barColour}`}
                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                data-testid="spec-idf-coverage-bar"
              />
            </div>
            <div className="mt-1 font-mono text-[10px] text-zinc-500">
              {covered}/{total} concepts covered ({pct}%)
            </div>
          </div>
          <button
            type="button"
            className="rounded-sm border border-zinc-200 bg-white p-1 text-zinc-500 hover:bg-zinc-50 disabled:opacity-40"
            onClick={onRefresh}
            disabled={status === 'loading'}
            data-testid="spec-idf-coverage-refresh"
            title="Re-run the audit (bypasses the 5-minute cache)."
            aria-label="Refresh IDF coverage"
          >
            <RefreshCw
              className={`h-3 w-3 ${status === 'loading' ? 'animate-spin' : ''}`}
              aria-hidden
            />
          </button>
        </div>
      )}

      {/* Empty states — tab-aware. */}
      {data && tab === 'idf' && !data.has_idf_summary && (
        <p
          className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-[11px] text-zinc-600"
          data-testid="spec-idf-coverage-empty-stage1"
        >
          Complete Stage 1 IDF analysis first to enable coverage
          checking. The audit reads tech-basics concepts, problem/
          solution prose, and key features from Stage 1.
        </p>
      )}
      {data && tab === 'claims' && data.has_frozen_claims === false && (
        <p
          className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-[11px] text-zinc-600"
          data-testid="spec-claim-coverage-empty-claims"
        >
          No frozen claims yet. Freeze the claim set in Stage 3
          and the claim-coverage audit will populate from each
          frozen claim's key terms.
        </p>
      )}
      {data && _isTabReady(data, tab) && !data.has_drafted_content && (
        <p
          className="rounded-sm border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-[11px] text-zinc-600"
          data-testid="spec-idf-coverage-empty-spec"
        >
          No drafted paragraphs yet. Generate at least one section
          and the audit will start surfacing missing concepts.
        </p>
      )}
      {/* BUG-046 — distinguish "Stage 1 done but extraction
          returned 0 concepts" from "Stage 1 not complete". The
          former usually means the source data is too sparse to
          tokenise; recovering needs a Stage 1 re-run, not a new
          generation pass. */}
      {data && _isTabReady(data, tab) && data.has_drafted_content
       && total === 0 && (
        <p
          className="rounded-sm border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
          data-testid="spec-idf-coverage-no-concepts"
        >
          {tab === 'idf'
            ? 'No concepts found in Stage 1 summary. Re-run Stage 1 analysis to populate the audit (or add term/feature data).'
            : 'No claim concepts extractable. Each frozen claim needs an analysis pass (key_terms) or non-empty claim text.'}
        </p>
      )}
      {/* FIX 4 — coverage 0% empty state. Distinct from the
          "no drafted content" message above: here the audit ran
          but ZERO concepts were anchored across both tabs. The
          drafter hasn't generated yet, or every generate so far
          missed the concept registry. Nudge them at the most
          productive next step (GE). */}
      {data && _isTabReady(data, tab) && total > 0 && covered === 0
       && data.has_drafted_content === false && (
        <p
          className="rounded-sm border border-zinc-300 bg-zinc-50 px-2 py-1.5 text-[11px] text-zinc-700"
          data-testid="spec-coverage-empty-zero"
        >
          Generate your spec sections to see coverage. Start with{' '}
          <strong>Generate everything</strong> in the header to
          populate every drafted section in one pass.
        </p>
      )}
      {/* FIX 4 — 100% empty state. Per-tab message because the
          drafter may have flipped to the inactive tab to check;
          surface readiness for the next workflow step. */}
      {data && _isTabReady(data, tab) && data.has_drafted_content
       && total > 0 && missing === 0 && (
        <p
          className="rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-800"
          data-testid="spec-idf-coverage-complete"
        >
          ✓ All {tab === 'idf' ? 'IDF concepts' : 'claim terms'} covered.
          {tab === 'claims' ? ' Ready for QC.' : ''}
        </p>
      )}

      {/* Missing group — expanded by default since it's the
          actionable surface. */}
      {groups.missing.length > 0 && (
        <CoverageGroup
          kind="missing"
          label={`MISSING (${missing})`}
          rows={groups.missing}
          expanded={missingOpen}
          onToggle={() => setMissingOpen((v) => !v)}
          onAddToSection={onAddToSection}
          onAddAllMissing={onAddAllMissing}
          targetLabel={targetLabel}
        />
      )}
      {/* Covered group — collapsed by default to keep the panel
          compact when most concepts are covered. */}
      {groups.covered.length > 0 && (
        <CoverageGroup
          kind="covered"
          label={`COVERED (${covered})`}
          rows={groups.covered}
          expanded={coveredOpen}
          onToggle={() => setCoveredOpen((v) => !v)}
          onAddToSection={null}
          onAddAllMissing={null}
          targetLabel={targetLabel}
        />
      )}
    </Dock>
  );
}

/** BUG-040 — readiness gate for the active tab. IDF is ready
 * when ``has_idf_summary``; Claims is ready when
 * ``has_frozen_claims`` (defaults to true on older deploys
 * that didn't surface the flag — degrades gracefully). */
function _isTabReady(
  data: { has_idf_summary?: boolean; has_frozen_claims?: boolean },
  tab: CoverageTab,
): boolean {
  if (tab === 'idf') return !!data.has_idf_summary;
  if (data.has_frozen_claims === false) return false;
  return true;
}

function _splitGroups(rows: SpecIdfConcept[]): {
  covered: SpecIdfConcept[]; missing: SpecIdfConcept[];
} {
  const covered: SpecIdfConcept[] = [];
  const missing: SpecIdfConcept[] = [];
  for (const r of rows) {
    if (r.status === 'covered') covered.push(r);
    else missing.push(r);
  }
  return { covered, missing };
}

function _relativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const delta = Math.max(0, Date.now() - then);
    const mins = Math.floor(delta / 60_000);
    if (mins < 1) return 'just now';
    if (mins === 1) return '1 minute ago';
    if (mins < 60) return `${mins} minutes ago`;
    const hours = Math.floor(mins / 60);
    if (hours === 1) return '1 hour ago';
    return `${hours} hours ago`;
  } catch {
    return '';
  }
}

function CoverageGroup({
  kind, label, rows, expanded, onToggle, onAddToSection,
  onAddAllMissing, targetLabel,
}: {
  kind: 'covered' | 'missing';
  label: string;
  rows: SpecIdfConcept[];
  expanded: boolean;
  onToggle: () => void;
  onAddToSection: ((concept: string) => void) | null;
  /** FIX 5 — batch handler. Null on covered groups. */
  onAddAllMissing: ((rows: SpecIdfConcept[]) => void) | null;
  /** FIX 2 — target section title for the per-row hint
   *  ("add to Detailed Description"). */
  targetLabel: string;
}): JSX.Element {
  return (
    <section className="mt-2 first:mt-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-800"
        data-testid={`spec-idf-coverage-group-${kind}-toggle`}
        aria-expanded={expanded}
      >
        <ChevronDown
          className={`h-3 w-3 transition-transform ${expanded ? '' : '-rotate-90'}`}
          aria-hidden
        />
        {label}
      </button>
      {expanded && (
        <>
          {/* FIX 5 — batch CTA above the missing list. Only
              renders on the missing group when there's at least
              one row to act on. */}
          {kind === 'missing' && onAddAllMissing && rows.length > 0 && (
            <button
              type="button"
              className="mt-1 inline-flex w-full items-center justify-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
              onClick={() => onAddAllMissing(rows)}
              data-testid="spec-coverage-add-all-missing"
              title={
                rows.length > BATCH_ADD_LIMIT
                  ? `Loads the first ${BATCH_ADD_LIMIT} of ${rows.length} `
                    + 'missing items into steering. Run another batch '
                    + 'after the next generate.'
                  : `Loads all ${rows.length} missing items into steering.`
              }
            >
              <ArrowRight className="h-3 w-3" />
              Add{rows.length <= BATCH_ADD_LIMIT ? ' all' : ` first ${BATCH_ADD_LIMIT} of ${rows.length}`}
              {' '}missing to {targetLabel}
            </button>
          )}
          <ul
            className="mt-1 space-y-1.5"
            data-testid={`spec-idf-coverage-group-${kind}-list`}
          >
            {rows.map((row) => (
              <CoverageRow
                key={`${row.idf_source}|${row.concept}`}
                row={row}
                onAddToSection={onAddToSection}
                targetLabel={targetLabel}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function CoverageRow({
  row, onAddToSection, targetLabel,
}: {
  row: SpecIdfConcept;
  onAddToSection: ((concept: string) => void) | null;
  /** FIX 2 — target section title for the missing-row hint. */
  targetLabel: string;
}): JSX.Element {
  const isCovered = row.status === 'covered';
  return (
    <li
      className="rounded-sm border border-zinc-200 bg-white px-2 py-1.5"
      data-testid={`spec-idf-coverage-row-${row.concept}`}
    >
      <div className="flex items-start gap-2">
        <span
          className={[
            'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm',
            isCovered
              ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200'
              : 'bg-rose-100 text-rose-700 ring-1 ring-rose-200',
          ].join(' ')}
          aria-hidden
        >
          {isCovered
            ? <Check className="h-2.5 w-2.5" />
            : <X className="h-2.5 w-2.5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-zinc-800">
            {row.concept}
          </p>
          {/* FIX 2 — covered rows already surface the sections
              that contain the term via ``found_in_sections``.
              Promote that line to ``covered in: ...`` so the
              parallel with the missing row's "add to ..." hint
              reads cleanly. */}
          {isCovered && row.found_in_sections.length > 0 && (
            <p className="mt-0.5 truncate text-[10px] text-emerald-700"
               title={`Covered in: ${row.found_in_sections.join(', ')}`}>
              covered in: {row.found_in_sections.slice(0, 3).join(', ')}
              {row.found_in_sections.length > 3 && '…'}
            </p>
          )}
          {/* FIX 2 — missing rows now state where the concept
              SHOULD land (the auto-routing target) alongside
              the IDF/claim source line. Drafters get one
              consistent visual contract: every row tells you
              "where it lives" or "where it should live". */}
          {!isCovered && (
            <>
              <p className="mt-0.5 flex items-center gap-1 text-[10px] text-zinc-500">
                <BookOpen className="h-2.5 w-2.5 text-zinc-400" aria-hidden />
                source: {_humanSource(row.idf_source)}
              </p>
              <p className="mt-0.5 flex items-center gap-1 text-[10px] text-rose-600">
                <ArrowRight className="h-2.5 w-2.5 text-rose-400" aria-hidden />
                add to {targetLabel}
              </p>
            </>
          )}
        </div>
        {!isCovered && onAddToSection && (
          <button
            type="button"
            className="ml-1 shrink-0 rounded-sm border border-zinc-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-zinc-700 hover:bg-zinc-50"
            onClick={() => onAddToSection(row.concept)}
            data-testid={`spec-idf-coverage-add-${row.concept}`}
            title={
              `Navigate to ${targetLabel} and load a steering directive `
              + `for "${row.concept}". You still click Generate manually.`
            }
          >
            Add to {targetLabel.split(' ')[0]}
          </button>
        )}
      </div>
    </li>
  );
}

function _humanSource(source: string): string {
  switch (source) {
    case 'glossary':          return 'IDF glossary';
    case 'key_concept':       return 'IDF key concepts';
    case 'technical_field':   return 'IDF technical field';
    case 'invention_summary': return 'IDF invention summary';
    default:
      // BUG-040 — claim sources arrive as ``claim_<N>``. Format
      // for human reading: "Claim 3" instead of the raw token.
      if (source.startsWith('claim_')) {
        const n = source.slice('claim_'.length);
        return `Claim ${n}`;
      }
      return source;
  }
}
